import { TeButton, TeContainer, TeContainerSlim, TeText, TeTooltip } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatDistanceToNow } from 'date-fns';

import { background } from '../../providers/theme/theme.index';
import { NotificationItem } from '../../api/generated/notification-inapp';
import { useUpdateNotificationStatus, useDeleteNotification } from './notifications.mutations';

interface Props {
  notifications: NotificationItem[];
}

// Styled wrapper for notification items with colored left edge
const NotificationItemWrapper = styled.div<{ isRead: boolean }>`
  position: relative;
  border-radius: 8px;
  background-color: ${background.secondary};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${(props) => (!props.isRead ? background.brand.primary : 'transparent')};
    border-radius: 8px 0 0 8px;
  }
`;

function RenderNotifications({ notifications }: Props) {
  const { t } = useTranslation(['notifications', 'common']);

  const updateNotificationStatus = useUpdateNotificationStatus();
  const deleteNotification = useDeleteNotification();

  const handleMarkAsRead = (id: string) => {
    updateNotificationStatus.mutate({ id, status: { read: true } });
  };

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id);
  };

  const getAppIcon = (app: string) => {
    // TODO: Implement proper app icon mapping
    return (
      <AppIconPlaceholder>
        <TeText size={12} color="text.inverse" weight={600}>
          {app.charAt(0).toUpperCase()}
        </TeText>
      </AppIconPlaceholder>
    );
  };

  return (
    <>
      {notifications &&
        notifications.map((notification) => {
          const title = notification.fields?.title || 'Notification Title';
          const summary =
            notification.fields?.summary ||
            'Notification content that provides the full message if it overflows in the drawer itself. Message should still be brief and have clear actionable intel for the end-user.';
          const appName = notification.app || 'Application name';
          const timeAgo = formatDistanceToNow(new Date(notification.timestamp), {
            addSuffix: true,
          });
          const isRead = notification.status?.read || false;

          return (
            <NotificationItemWrapper key={notification.id} isRead={isRead}>
              <TeContainer
                grow
                borderRadius={8}
                backgroundColor="background.secondary"
                alignItems="center"
                padding="s"
                gap="s"
              >
                {/* Left side - App icon and content */}
                <TeContainerSlim column gap="xs2" grow alignItems="flex-start">
                  <TeContainerSlim gap="xs" alignItems="center">
                    {getAppIcon(appName)}
                    <TeContainerSlim column gap="xs2">
                      <TeText size={12} color="text.secondary">
                        {appName}
                      </TeText>
                      <TeText size={14} weight={600} color="text.primary">
                        {title}
                      </TeText>
                    </TeContainerSlim>
                  </TeContainerSlim>
                  <TeText size={12} color="text.secondary" style={{ lineHeight: '1.4' }}>
                    {summary}
                  </TeText>
                </TeContainerSlim>

                {/* Right side - Actions */}
                <TeContainerSlim column gap="xs" alignItems="flex-end">
                  <TeText size={12} color="text.secondary">
                    {timeAgo}
                  </TeText>
                  <TeContainerSlim gap="xs2">
                    <TeButton
                      key={`mark-as-read`}
                      popkey={`mark-as-read-${notification.id}`}
                      variant="neutral"
                      size="xs"
                      icon="mail"
                      teclick={() => handleMarkAsRead(notification.id)}
                      title={t('markAsRead')}
                    />
                    <TeButton
                      key={`delete`}
                      popkey={`delete-${notification.id}`}
                      variant="neutral"
                      size="xs"
                      icon="delete"
                      teclick={() => handleDelete(notification.id)}
                      title={t('delete')}
                    />
                    <TeTooltip for={`mark-as-read-${notification.id}`} text={t('markAsRead')} />
                    <TeTooltip for={`delete-${notification.id}`} text={t('delete')} />
                    <TeTooltip for={`view-${notification.id}`} text={t('view')} />
                  </TeContainerSlim>
                </TeContainerSlim>
              </TeContainer>
            </NotificationItemWrapper>
          );
        })}
    </>
  );
}

export default RenderNotifications;

const AppIconPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${background.brand.primary};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
